import { useCallback, useMemo, useState } from 'react'
import { getStudentApplicationCountryLabel } from '../constants/studentApplicationCountries'
import { studentsStore } from '../model/studentsStore'
import { studentsFiltersStore } from '../model/studentsFiltersStore'

const FILTER_TITLES = {
	studentNationality: 'Country of application',
	grade: 'Grade',
	assignmentStatus: 'Assignment status',
	course: 'Course',
}

const toArr = v => (Array.isArray(v) ? v : v ? [v] : [])

const valueToLabel = (filterKey, v) => {
	if (!v) return ''
	if (filterKey === 'studentNationality' && typeof v === 'string') {
		return getStudentApplicationCountryLabel(v)
	}
	if (typeof v === 'string') return v
	return v.label || v.value || String(v)
}

export const useFiltersSortBarStudents = () => {
	const [extraItems, setExtraItems] = useState([])

	const hasFilters = studentsStore.hasPanelFilters
	const pf = studentsStore.panelFilters
	const sortKey = studentsStore.sortKey

	const baseItems = useMemo(() => {
		if (!hasFilters) return []

		const out = [{ id: 'all', label: 'All' }]

		Object.keys(FILTER_TITLES).forEach(key => {
			const selected = toArr(pf?.[key])
			if (!selected.length) return

			const labels = selected.map(v => valueToLabel(key, v)).filter(Boolean)
			if (!labels.length) return

			out.push({
				id: `filter:${key}`,
				filterKey: key,
				label: `${FILTER_TITLES[key]}: ${labels.join(', ')}`,
			})
		})

		return out
	}, [
		hasFilters,
		pf,
	])

	const activeIds = useMemo(() => {
		if (!hasFilters) return ['all']
		return baseItems
			.filter(i => String(i.id).startsWith('filter:'))
			.map(i => i.id)
	}, [hasFilters, baseItems])

	const isVisible = hasFilters || !!sortKey

	const onSortChange = useCallback(opt => {
		if (!opt?.key || !opt?.dir) return
		studentsStore.setSort(opt.key, opt.dir)
	}, [])

	const clearSort = useCallback(item => {
		setExtraItems(prev => prev.filter(x => x.id !== item.id))
		if (item?.id === 'sort') {
			studentsStore.clearSort()
		}
	}, [])

	const clearAllPanelFilters = useCallback(() => {
		studentsStore.clearPanelFilters()
		Object.keys(FILTER_TITLES).forEach(key => {
			studentsFiltersStore.setFilter(key, [])
		})
	}, [])

	const removeOnePanelFilter = useCallback(filterKey => {
		if (!filterKey) return

		const next = {
			...studentsStore.panelFilters,
			[filterKey]: [],
		}

		studentsStore.applyPanelFilters(next)
		studentsFiltersStore.setFilter(filterKey, [])
	}, [])

	const onBaseChange = useCallback(
		({ lastToggledId, isActiveNow, item }) => {
			if (String(lastToggledId) === 'all') {
				clearAllPanelFilters()
				return
			}

			const filterKey =
				item?.filterKey || String(lastToggledId || '').replace('filter:', '')

			if (!isActiveNow) {
				removeOnePanelFilter(filterKey)
			}
		},
		[clearAllPanelFilters, removeOnePanelFilter]
	)

	const onRemoveExtra = useCallback(
		item => {
			clearSort(item)
		},
		[clearSort]
	)

	return {
		isVisible,
		baseItems,
		activeIds,
		extraItems,
		setExtraItems,
		onBaseChange,
		onRemoveExtra,
		onSortChange,
	}
}
